import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { fetchMyAnnonces, deleteMyAnnonce, toggleAutoBump, translateAnnonce, setMultiCountry, verifyProSubscription, markAnnonceSold, API } from "@/lib/api";
import { SoldSurveyModal } from "@/components/SoldSurveyModal";
import { BoostModal } from "@/components/BoostModal";
import { BoostStatsPanel } from "@/components/BoostStatsPanel";
import { fmtPrice, withWatermark } from "@/lib/utils";
import AccountSidebar from "@/components/AccountSidebar";
import EditMyAnnonceModal from "@/components/EditMyAnnonceModal";
import ProStatsPanel from "@/components/ProStatsPanel";
import { Plus, Pencil, Trash2, MapPin, BadgeCheck, Sparkles, Hand, Wand2, Rocket, Lock, Languages, Globe2, Loader2, Flame, Tag } from "lucide-react";

const UEMOA = [
  { code: "SN", name: "Senegal" }, { code: "ML", name: "Mali" },
  { code: "BF", name: "Burkina Faso" }, { code: "NE", name: "Niger" },
  { code: "BJ", name: "Benin" }, { code: "TG", name: "Togo" }, { code: "CI", name: "Cote d'Ivoire" },
];

export default function MesAnnoncesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [claimRef, setClaimRef] = useState("");
  const [claimPhone, setClaimPhone] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [aiCredits, setAiCredits] = useState(null); // {free_credits, is_pro}
  const [boosting, setBoosting] = useState(null);
  const [sold, setSold] = useState(null); // {annonce, surveyDone}

  const onSold = async (a) => {
    try {
      const r = await markAnnonceSold(a.id);
      setItems((arr) => arr.map((x) => (x.id === a.id ? { ...x, is_sold: true, sold_at: r.sold_at } : x)));
      setSold({ annonce: a, surveyDone: r.survey_done });
    } catch (err) { toast.error(err.response?.data?.detail || "Echec"); }
  };
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const tx = params.get("tx");
    if (params.get("paiement") && tx) {
      verifyProSubscription(tx)
        .then((r) => { if (r.activated) toast.success("Paiement confirme — votre annonce est a la une !"); else toast.info("Paiement en cours de verification…"); })
        .catch(() => toast.error("Verification du paiement impossible"))
        .finally(() => { fetchMyAnnonces().then(setItems); ["paiement", "tx", "status", "dryrun", "free"].forEach((k) => params.delete(k)); setParams(params, { replace: true }); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const bid = params.get("boost");
    if (bid && items.length && !boosting) {
      const a = items.find((x) => x.id === bid);
      if (a) { setBoosting(a); params.delete("boost"); setParams(params, { replace: true }); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    fetchMyAnnonces().then(setItems).finally(() => setLoading(false));
    axios.get(`${API}/me/ai-credits`, { withCredentials: true })
      .then((r) => setAiCredits(r.data))
      .catch(() => setAiCredits(null));
  }, []);

  const onSaved = (updated) => {
    setItems((arr) => arr.map((a) => (a.id === updated.id ? updated : a)));
  };

  const onClaim = async (e) => {
    e.preventDefault();
    if (!claimRef.trim() || !claimPhone.trim()) return;
    setClaiming(true);
    try {
      await axios.post(`${API}/me/annonces/claim`, {
        ref: claimRef.trim().toUpperCase(),
        phone: claimPhone.trim(),
      }, { withCredentials: true });
      toast.success("Annonce reclamee. Rechargement…");
      const fresh = await fetchMyAnnonces();
      setItems(fresh);
      setClaimRef(""); setClaimPhone("");
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail
        || (status === 404 ? "Aucune annonce avec cette reference" :
            status === 409 ? "Cette annonce appartient deja a un autre utilisateur" :
            status === 403 ? "Le numero ne correspond pas" :
            "Echec de la reclamation");
      toast.error(typeof msg === "string" ? msg : "Echec de la reclamation");
    } finally { setClaiming(false); }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Supprimer definitivement "${a.title}" ?\nCette action est irreversible.`)) return;
    try {
      await deleteMyAnnonce(a.id);
      setItems((arr) => arr.filter((x) => x.id !== a.id));
      toast.success("Annonce supprimee");
    } catch (err) {
      const msg = err.response?.data?.detail || "Echec de la suppression";
      toast.error(typeof msg === "string" ? msg : "Echec de la suppression");
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="mes-annonces-page">
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Mes annonces</h1>
      <p className="text-[var(--ink-soft)] mt-2">Toutes les annonces que vous avez publiees.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <AccountSidebar />

        <section>
          <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
            <div className="font-bold" data-testid="mes-annonces-count">
              {loading ? "Chargement..." : `${items.length} annonce${items.length > 1 ? "s" : ""}`}
            </div>
            <Link to="/deposer" className="btn-primary inline-flex items-center gap-2" data-testid="mes-annonces-new">
              <Plus size={16} /> Nouvelle annonce
            </Link>
          </div>

          {aiCredits?.is_pro && <ProStatsPanel />}


          {aiCredits && !aiCredits.is_pro && aiCredits.free_credits > 0 && (
            <div className="glass-card p-4 mb-5 bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200" data-testid="ai-credits-banner">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 grid place-items-center text-white flex-shrink-0">
                  <Wand2 size={18} />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm">
                    {aiCredits.free_credits === 3 ? "Cadeau de bienvenue : " : ""}
                    {aiCredits.free_credits} boost{aiCredits.free_credits > 1 ? "s" : ""} IA gratuit{aiCredits.free_credits > 1 ? "s" : ""} disponible{aiCredits.free_credits > 1 ? "s" : ""}
                  </div>
                  <p className="text-xs text-[var(--ink-soft)] mt-0.5">
                    Boostez le titre, la description ou ajustez le prix de vos annonces avec l&apos;IA Claude.
                    En moyenne <b>+3x plus de contacts</b> chez nos vendeurs.
                  </p>
                  <Link to="/abonnements" className="text-xs font-bold text-orange-700 hover:underline mt-1 inline-block" data-testid="ai-credits-pro-cta">
                    Passer Pro pour des boosts illimites →
                  </Link>
                </div>
              </div>
            </div>
          )}
          {aiCredits && !aiCredits.is_pro && aiCredits.free_credits === 0 && (
            <div className="glass-card p-4 mb-5 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="ai-credits-exhausted-banner">
              <div className="flex items-center gap-3 flex-wrap">
                <Wand2 size={18} className="text-orange-600 flex-shrink-0" />
                <div className="text-sm flex-1">
                  Vos boosts IA gratuits sont epuises. <Link to="/abonnements" className="font-bold text-orange-700 hover:underline">Passer Fodindin Pro</Link> pour des boosts illimites.
                </div>
              </div>
            </div>
          )}

          <details className="glass-card p-4 mb-5" data-testid="claim-orphan-panel">
            <summary className="cursor-pointer text-sm font-bold text-orange-700 inline-flex items-center gap-2">
              <Hand size={14} /> Une de vos annonces ne s&apos;affiche pas ici ? Reclamez-la
            </summary>
            <p className="text-xs text-[var(--ink-soft)] mt-2">
              Si vous avez depose une annonce sans etre connecte (session expiree), entrez sa reference
              <b> FD-XXXXXX</b> et le <b>numero de telephone</b> utilise lors du depot pour l&apos;associer
              a votre compte.
            </p>
            <form onSubmit={onClaim} className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-center">
              <input
                value={claimRef}
                onChange={(e) => setClaimRef(e.target.value)}
                placeholder="FD-000123"
                className="!py-2 !px-3 rounded-xl border border-[var(--line)] focus:border-orange-400 outline-none"
                data-testid="claim-ref-input"
              />
              <input
                value={claimPhone}
                onChange={(e) => setClaimPhone(e.target.value)}
                placeholder="+225 0X XX XX XX XX"
                className="!py-2 !px-3 rounded-xl border border-[var(--line)] focus:border-orange-400 outline-none"
                data-testid="claim-phone-input"
              />
              <button type="submit" disabled={claiming || !claimRef.trim() || !claimPhone.trim()}
                      className="btn-primary !py-2 inline-flex items-center gap-1.5 justify-center"
                      data-testid="claim-submit-btn">
                <Hand size={14} /> {claiming ? "Verification..." : "Reclamer"}
              </button>
            </form>
          </details>

          {!loading && items.length === 0 ? (
            <div className="glass-card p-12 text-center" data-testid="mes-annonces-empty">
              <div className="font-bold text-lg mb-2">Aucune annonce publiee</div>
              <p className="text-sm text-[var(--ink-soft)] mb-5">Commencez par deposer votre premiere annonce.</p>
              <Link to="/deposer" className="btn-primary">Deposer une annonce</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {items.map((a) => (
                <OwnerCard key={a.id} a={a} isPro={!!aiCredits?.is_pro} onEdit={() => setEditing(a)} onDelete={() => onDelete(a)} onBoost={() => setBoosting(a)} onSold={() => onSold(a)} />
              ))}
            </div>
          )}
        </section>
      </div>

      {boosting && <BoostModal annonce={boosting} onClose={() => setBoosting(null)} />}
      {sold && <SoldSurveyModal annonce={sold.annonce} surveyDone={sold.surveyDone} onClose={() => setSold(null)} onDelete={async () => { const a = sold.annonce; setSold(null); await onDelete(a); }} />}
      <EditMyAnnonceModal
        key={editing?.id}
        annonce={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />
    </main>
  );
}

function OwnerCard({ a, isPro, onEdit, onDelete, onBoost, onSold }) {
  const boostActive = a.boosted_until && new Date(a.boosted_until) > new Date();
  const img = (a.images && a.images[0]) || a.image || "/placeholder.png";
  const [autoBump, setAutoBump] = useState(!!a.auto_bump);
  const [toggling, setToggling] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(!!a.translations);
  const [showCountries, setShowCountries] = useState(false);
  const [extraCountries, setExtraCountries] = useState(a.extra_countries || []);
  const [savingCountries, setSavingCountries] = useState(false);

  const requirePro = () => {
    toast.info("Fonction reservee aux abonnes Fodindin Pro.");
    return false;
  };

  const onTranslate = async () => {
    if (!isPro) return requirePro();
    setTranslating(true);
    try {
      await translateAnnonce(a.id);
      setTranslated(true);
      toast.success("Annonce traduite en Wolof et Bambara ! Visible sur la page de l'annonce 🌍");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Echec de la traduction. Reessayez.");
    } finally { setTranslating(false); }
  };

  const toggleCountry = (code) => {
    setExtraCountries((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  };

  const saveCountries = async () => {
    setSavingCountries(true);
    try {
      const res = await setMultiCountry(a.id, extraCountries);
      setExtraCountries(res.extra_countries);
      setShowCountries(false);
      toast.success(res.extra_countries.length
        ? `Annonce publiee dans ${res.extra_countries.length} pays supplementaire(s) 🌍`
        : "Publication limitee au pays d'origine");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Echec de la mise a jour");
    } finally { setSavingCountries(false); }
  };

  const baseCountry = (a.country || "CI").toUpperCase();

  const onToggleBump = async () => {
    if (!isPro) {
      toast.info("L'auto-bump 48h est reserve aux abonnes Fodindin Pro.");
      return;
    }
    setToggling(true);
    try {
      const res = await toggleAutoBump(a.id, !autoBump);
      setAutoBump(res.auto_bump);
      toast.success(res.auto_bump
        ? "Auto-bump active : votre annonce remontera en tete toutes les 48h 🚀"
        : "Auto-bump desactive");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Echec de la mise a jour");
    } finally { setToggling(false); }
  };

  return (
    <article className="glass-card overflow-hidden flex flex-col" data-testid={`owner-card-${a.id}`}>
      <Link to={`/annonces/${a.id}`} className="block relative">
        <img src={withWatermark(img)} alt={a.title} className="w-full h-44 object-cover" />
        <div className="absolute top-2 left-2 flex gap-1">
          {a.premium && (
            <span className="text-[10px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
              <Sparkles size={10} /> PREMIUM
            </span>
          )}
          {a.verified && (
            <span className="text-[10px] font-bold bg-green-600 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
              <BadgeCheck size={10} /> VERIFIE
            </span>
          )}
        </div>
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <Link to={`/annonces/${a.ref || a.id}`} className="font-bold text-base line-clamp-2 hover:text-orange-600 transition" data-testid={`owner-card-title-${a.id}`}>
          {a.title}
        </Link>
        {a.ref ? (
          <div className="mt-1 text-[10px] font-bold tracking-wider text-orange-600 inline-flex items-center gap-1 self-start bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200"
               data-testid={`owner-card-ref-${a.id}`}>
            Ref : {a.ref}
          </div>
        ) : null}
        {a.blocked ? (
          <div className="mt-1 text-[10px] font-bold tracking-wider text-red-700 inline-flex items-center gap-1 self-start bg-red-50 px-2 py-0.5 rounded-full border border-red-200"
               data-testid={`owner-card-blocked-${a.id}`}>
            BLOQUEE
          </div>
        ) : null}
        <div className="text-[11px] text-[var(--ink-soft)] mt-1 inline-flex items-center gap-1">
          <MapPin size={11} /> {a.city}{a.quartier ? ` · ${a.quartier}` : ""}
        </div>
        <div className="mt-2 font-extrabold text-orange-600 text-lg" data-testid={`owner-card-price-${a.id}`}>
          {fmtPrice(a.price, a.price_unit)}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl border border-orange-300 text-orange-700 hover:bg-orange-50 transition"
            data-testid={`owner-edit-btn-${a.id}`}
          >
            <Pencil size={14} /> Modifier
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl border border-red-300 text-red-700 hover:bg-red-50 transition"
            data-testid={`owner-delete-btn-${a.id}`}
          >
            <Trash2 size={14} /> Supprimer
          </button>
        </div>
        {a.is_sold ? (
          <div className="mt-2 inline-flex w-full items-center justify-center gap-1.5 text-xs font-black px-3 py-2 rounded-xl bg-emerald-600 text-white" data-testid={`owner-sold-badge-${a.id}`}><Tag size={13} /> VENDU</div>
        ) : (
          <button type="button" onClick={onSold} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-sm font-bold px-3 py-2 rounded-xl border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 transition" data-testid={`owner-sold-btn-${a.id}`}>
            <Tag size={14} /> Marquer comme vendu
          </button>
        )}
        <button
          type="button"
          onClick={onBoost}
          className={`mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl w-full text-white shadow-md transition ${boostActive ? "bg-gradient-to-r from-emerald-500 to-teal-500" : "bg-gradient-to-r from-rose-500 to-orange-500 hover:shadow-lg"}`}
          data-testid={`owner-boost-btn-${a.id}`}
        >
          <Flame size={14} /> {boostActive ? `A la une jusqu'au ${new Date(a.boosted_until).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : "Booster 24h / 48h / 72h — des 500 FCFA"}
        </button>
        <BoostStatsPanel annonce={a} />
        <button
          type="button"
          onClick={onToggleBump}
          disabled={toggling}
          className={`mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl border transition w-full ${
            autoBump
              ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950 border-amber-400"
              : isPro
                ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                : "border-[var(--line)] text-[var(--ink-soft)]"
          }`}
          title={isPro ? "Remonte automatiquement votre annonce en tete des resultats toutes les 48h" : "Reserve aux abonnes Pro"}
          data-testid={`auto-bump-toggle-${a.id}`}
        >
          {isPro ? <Rocket size={13} /> : <Lock size={13} />}
          {autoBump ? "Auto-bump 48h : ACTIF" : "Activer l'auto-bump 48h"}
          {!isPro && <span className="text-[9px] font-extrabold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">PRO</span>}
        </button>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onTranslate}
            disabled={translating}
            className={`inline-flex items-center justify-center gap-1.5 text-xs font-bold px-2 py-2 rounded-xl border transition ${
              translated
                ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                : isPro ? "border-sky-300 text-sky-700 hover:bg-sky-50" : "border-[var(--line)] text-[var(--ink-soft)]"
            }`}
            title="Traduction IA en Wolof (Senegal) et Bambara (Mali)"
            data-testid={`translate-btn-${a.id}`}
          >
            {translating ? <Loader2 size={13} className="animate-spin" /> : <Languages size={13} />}
            {translated ? "Traduit WO/BM ✓" : "Traduire WO/BM"}
            {!isPro && <Lock size={11} />}
          </button>
          <button
            type="button"
            onClick={() => (isPro ? setShowCountries((v) => !v) : requirePro())}
            className={`inline-flex items-center justify-center gap-1.5 text-xs font-bold px-2 py-2 rounded-xl border transition ${
              extraCountries.length
                ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                : isPro ? "border-violet-300 text-violet-700 hover:bg-violet-50" : "border-[var(--line)] text-[var(--ink-soft)]"
            }`}
            title="Publier cette annonce dans d'autres pays UEMOA"
            data-testid={`multi-country-btn-${a.id}`}
          >
            <Globe2 size={13} />
            {extraCountries.length ? `Multi-pays (${extraCountries.length}) ✓` : "Multi-pays"}
            {!isPro && <Lock size={11} />}
          </button>
        </div>
        {showCountries && (
          <div className="mt-2 rounded-xl border border-violet-200 bg-violet-50/50 p-3" data-testid={`country-picker-${a.id}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-violet-700 mb-2">Publier aussi dans :</div>
            <div className="flex flex-wrap gap-1.5">
              {UEMOA.filter((c) => c.code !== baseCountry).map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => toggleCountry(c.code)}
                  className={`chip text-xs ${extraCountries.includes(c.code) ? "active" : ""}`}
                  data-testid={`country-chip-${c.code}-${a.id}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={saveCountries}
              disabled={savingCountries}
              className="btn-primary w-full mt-2 !py-1.5 text-xs"
              data-testid={`save-countries-btn-${a.id}`}
            >
              {savingCountries ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        )}
      </div>
    </article>
  );
}
